import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [isManualOverride, setIsManualOverride] = useState(false);

  // Determine theme based on time of day
  const getTimeBasedTheme = () => {
    const hour = new Date().getHours();
    // Dark mode: 6PM (18:00) to 6AM (06:00)
    return (hour >= 18 || hour < 6) ? 'dark' : 'light';
  };

  // Initialize theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('ux-theme');
    const savedOverride = localStorage.getItem('ux-theme-override');
    
    if (savedOverride === 'true' && savedTheme) {
      // User has manually set a preference
      setTheme(savedTheme);
      setIsManualOverride(true);
    } else {
      // Use time-based theme
      setTheme(getTimeBasedTheme());
    }
  }, []);

  // Update time-based theme periodically (check every minute)
  useEffect(() => {
    if (isManualOverride) return;

    const interval = setInterval(() => {
      const newTheme = getTimeBasedTheme();
      if (newTheme !== theme) {
        setTheme(newTheme);
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [theme, isManualOverride]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
  }, [theme]);

  // Toggle theme manually
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setIsManualOverride(true);
    localStorage.setItem('ux-theme', newTheme);
    localStorage.setItem('ux-theme-override', 'true');
  };

  // Reset to auto (time-based)
  const resetToAuto = () => {
    setIsManualOverride(false);
    localStorage.removeItem('ux-theme');
    localStorage.removeItem('ux-theme-override');
    setTheme(getTimeBasedTheme());
  };

  const value = {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    isManualOverride,
    toggleTheme,
    resetToAuto,
    setTheme: (newTheme) => {
      setTheme(newTheme);
      setIsManualOverride(true);
      localStorage.setItem('ux-theme', newTheme);
      localStorage.setItem('ux-theme-override', 'true');
    }
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Hook for admin pages - always returns light mode
export const useAdminTheme = () => {
  return {
    theme: 'light',
    isDark: false,
    toggleTheme: () => {},
    resetToAuto: () => {},
    setTheme: () => {}
  };
};

export default ThemeContext;
